import * as React from 'react';

import {
  Button,
  cn,
  DataGrid,
  Pagination,
  type PaginationProps,
  Content,
  Footer,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Plus } from 'lucide-react';
import { useLocation } from 'wouter';

import * as state from '../../_state/automationList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { automationsRoutes } from '../../routes';
import { AutomationRecord } from '../../types';

import { AutomationBulkToolbar } from './AutomationBulkToolbar';
import { AutomationFilterBar } from './AutomationFilterBar';
import { useAutomationsListColumns } from './hooks/useAutomationsListColumns';

export const AutomationListView: React.FC = () => {
  const t = useTranslations();
  const [, navigate] = useLocation();
  const toast = useToast();

  // State atoms
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const searchTerm = useAtomValue(state.$searchTerm);
  const selectedStatus = useAtomValue(state.$selectedStatus);
  const selectedTriggerType = useAtomValue(state.$selectedTriggerType);

  // Remote data
  const {
    data,
    isLoading,
    error,
    refetch: refetchAutomations,
  } = useAtomValue(state.$remoteAutomations);

  // Actions
  const deleteAutomation = useSetAtom(state.$deleteAutomation);
  const activateAutomation = useSetAtom(state.$activateAutomation);
  const pauseAutomation = useSetAtom(state.$pauseAutomation);
  const duplicateAutomation = useSetAtom(state.$duplicateAutomation);

  const automations = data?.automations || [];
  const total = data?.total || 0;
  const selectedRows = useMemoDataGridRows(automations, selectedKeys);

  const handleRefetch = React.useCallback(() => {
    refetchAutomations();
  }, [refetchAutomations]);

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleCreateNew = React.useCallback(() => {
    navigate(automationsRoutes.create);
  }, [navigate]);

  const handleActivate = React.useCallback(
    async (automationId: string) => {
      try {
        await activateAutomation({ id: automationId });
        toast.success(t.detail.messages.activated);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : t.detail.messages.activateFailed(''),
        );
      }
    },
    [activateAutomation, toast, t],
  );

  const handlePause = React.useCallback(
    async (automationId: string) => {
      try {
        await pauseAutomation({ id: automationId });
        toast.success(t.detail.messages.paused);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : t.detail.messages.pauseFailed(''),
        );
      }
    },
    [pauseAutomation, toast, t],
  );

  const handleDuplicate = React.useCallback(
    async (automationId: string) => {
      try {
        const result = await duplicateAutomation({ id: automationId });
        toast.success(t.detail.messages.duplicated);
        const newId = result?.automationsAutomationDuplicate?.id;
        if (newId) {
          navigate(automationsRoutes.detail.replace(':id', newId));
        }
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : t.detail.messages.duplicateFailed(''),
        );
      }
    },
    [duplicateAutomation, toast, navigate, t],
  );

  const handleDelete = React.useCallback(
    async (automationId: string) => {
      try {
        await deleteAutomation({ id: automationId });
        toast.success(t.detail.messages.deleted);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : t.detail.messages.deleteFailed(''),
        );
      }
    },
    [deleteAutomation, toast, t],
  );

  const handleClearSelection = React.useCallback(() => {
    setSelectedKeys(new Set());
  }, [setSelectedKeys]);

  const { columns } = useAutomationsListColumns({
    onActivate: handleActivate,
    onPause: handlePause,
    onDelete: handleDelete,
    onDuplicate: handleDuplicate,
    onRefetch: handleRefetch,
  });

  const hasFiltersActive = Boolean(
    searchTerm || selectedStatus || selectedTriggerType,
  );

  const emptyPanelProps = {
    title: t.list.noResults,
    description: <span>{t.list.noResultsDescription}</span>,
    action: (
      <Button
        onPress={handleCreateNew}
        variant="solid"
        color="primary"
        icon={<Plus />}
      >
        {t.list.createNew}
      </Button>
    ),
    centered: true,
  };

  const emptyResultsPanelProps = {
    title: t.list.noResultsFiltered,
    description: t.list.noResultsFilteredDescription,
    action: (
      <Button
        onPress={() => {
          navigate(automationsRoutes.list);
        }}
        variant="solid"
        color="primary"
      >
        {t.list.clearFilters}
      </Button>
    ),
    centered: true,
  };

  if (error) {
    return (
      <div className="p-4 text-failure">
        {t.list.errorLoading(error.message)}
      </div>
    );
  }

  return (
    <div className={cn('@AutomationListView', 'flex h-full flex-col')}>
      <AutomationFilterBar onCreateNew={handleCreateNew} />
      <Content className="h-full border-t border-neutral/10">
        <DataGrid<AutomationRecord>
          columns={columns}
          dataSource={automations}
          rowKey="id"
          aria-label={t.list.ariaLabel}
          selectionMode="multiple"
          onSelectionChange={setSelectedKeys}
          selectedKeys={selectedKeys}
          fullWidth
          isLoading={isLoading}
          emptyPanelProps={
            hasFiltersActive ? emptyResultsPanelProps : emptyPanelProps
          }
        />
      </Content>
      {total > limit && (
        <Footer>
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        </Footer>
      )}
      <AutomationBulkToolbar
        selectedRows={selectedRows}
        onClearSelection={handleClearSelection}
      />
    </div>
  );
};
